import React, { useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import {
    Box,
    Button,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material'
import { StudentRatingState, StudentWeekScoreState } from '../../../../features/types'
import { ratingApiSlice } from '../../../../features/rating/ratingSlice'
import { filterField } from '../../../../components/filterSelect/FilterSelect'
import { tableStyles, ui } from '../../../../utils/uiTokens'
import AllWeeksSummary from './AllWeeksSummary'

interface Props {
    students: StudentRatingState[]
    monthNumber?: number
    weekNumber?: number
    groupId?: number
    courseSubjectId?: number
    filter?: string
}

const PRACTICE_OPTIONS = [
    { value: '', labelId: 'notMarked', point: null as number | null },
    { value: 'WITH_CAMERA', labelId: 'withCamera', point: 20 },
    { value: 'WITHOUT_CAMERA', labelId: 'withoutCamera', point: 10 },
    { value: 'ABSENT', labelId: 'absent', point: 0 },
]

const POINTS: Record<string, number> = { WITH_CAMERA: 20, WITHOUT_CAMERA: 10, ABSENT: 0 }
const pointsOf = (status?: string | null) => (status ? POINTS[status] ?? 0 : 0)
const WEEK_MAX = 40

type RowStatus = 'saving' | 'saved' | 'error'

const PracticeTable: React.FC<Props> = ({ students = [], monthNumber, weekNumber, groupId, courseSubjectId, filter }) => {
    const { formatMessage } = useIntl()
    const [saveWeekScore] = ratingApiSlice.useSaveWeekScoreMutation()
    const [draft, setDraft] = useState<Record<number, Partial<StudentWeekScoreState>>>({})
    const [status, setStatus] = useState<Record<number, RowStatus>>({})
    const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({})

    const valueOf = (student: StudentRatingState, field: keyof StudentWeekScoreState) => {
        const local = draft[student.userSubjectEnrollId]
        if (local && local[field] !== undefined) return local[field]
        if (field === 'zachetPoint') return student.zachetPoint || ''
        if (field === 'practiceStatus') return student.practiceStatus || ''
        if (field === 'practice2Status') return student.practice2Status || ''
        return ''
    }

    const saveRow = async (student: StudentRatingState) => {
        const id = student.userSubjectEnrollId
        const local = draft[id] || {}
        const zachet = local.zachetPoint ?? student.zachetPoint
        const hasZachet = zachet !== undefined && zachet !== null && String(zachet) !== ''

        setStatus((s) => ({ ...s, [id]: 'saving' }))
        try {
            await saveWeekScore({
                userSubjectEnrollId: id,
                monthNumber: monthNumber || 0,
                weekNumber: weekNumber || 0,
                zachetPoint: hasZachet ? Number(zachet) : null,
                practiceStatus: (local.practiceStatus ?? student.practiceStatus) || '',
                practice2Status: (local.practice2Status ?? student.practice2Status) || '',
            }).unwrap()
            setStatus((s) => ({ ...s, [id]: 'saved' }))
        } catch (error) {
            setStatus((s) => ({ ...s, [id]: 'error' }))
        }
    }

    // Статус выбирается редко - сохраняем сразу; зачет печатают - ждём паузы
    const change = (
        student: StudentRatingState,
        field: keyof StudentWeekScoreState,
        value: string | number,
        debounce = false,
    ) => {
        const id = student.userSubjectEnrollId
        setDraft((d) => ({ ...d, [id]: { ...d[id], [field]: value } }))

        if (timers.current[id]) clearTimeout(timers.current[id])
        if (debounce) {
            timers.current[id] = setTimeout(() => saveRow({ ...student, [field]: value } as StudentRatingState), 600)
        } else {
            saveRow({ ...student, [field]: value } as StudentRatingState)
        }
    }

    if (!monthNumber) {
        return (
            <Box sx={{ py: 6, textAlign: 'center', color: ui.muted, fontSize: '14px' }}>
                {formatMessage({ id: 'chooseMonth' })}
            </Box>
        )
    }

    // «Барлық апталар» - сводка по месяцу только для чтения
    if (!weekNumber) {
        return (
            <AllWeeksSummary
                monthNumber={monthNumber}
                groupId={groupId}
                courseSubjectId={courseSubjectId}
                filter={filter}
            />
        )
    }

    const optionLabel = (labelId: string, point: number | null) => {
        const text = formatMessage({ id: labelId })
        if (point === null) return text
        return `${text} · ${point > 0 ? `+${point}` : point}`
    }

    const rowStatus = (student: StudentRatingState) => {
        const st = status[student.userSubjectEnrollId]
        if (st === 'saving') return <Box sx={{ fontSize: '12px', color: ui.muted }}>{formatMessage({ id: 'saving' })}</Box>
        if (st === 'saved') return <Box sx={{ fontSize: '12px', color: '#00B998' }}>✓ {formatMessage({ id: 'saved' })}</Box>
        if (st === 'error')
            return (
                <Button size="small" color="error" onClick={() => saveRow(student)}>
                    {formatMessage({ id: 'retry' })}
                </Button>
            )
        return null
    }

    return (
        <TableContainer component={Paper} sx={tableStyles.container} elevation={0}>
            <Table>
                <TableHead sx={tableStyles.head}>
                    <TableRow>
                        <TableCell>{formatMessage({ id: 'student' })}</TableCell>
                        <TableCell>{formatMessage({ id: 'practice1' })}</TableCell>
                        <TableCell>{formatMessage({ id: 'practice2' })}</TableCell>
                        <TableCell>{formatMessage({ id: 'practiceWeekScore' })}</TableCell>
                        <TableCell>{formatMessage({ id: 'zachet' })}</TableCell>
                        <TableCell align="right" />
                    </TableRow>
                </TableHead>

                <TableBody>
                    {students.map((student) => {
                        const weekPoint =
                            pointsOf(valueOf(student, 'practiceStatus') as string) +
                            pointsOf(valueOf(student, 'practice2Status') as string)
                        return (
                            <TableRow key={student.userSubjectEnrollId} sx={tableStyles.row}>
                                <TableCell>
                                    {student.firstName} {student.lastName}
                                    <Box sx={{ fontSize: '12px', color: ui.muted }}>{student.groupTitle}</Box>
                                </TableCell>

                                {(['practiceStatus', 'practice2Status'] as const).map((field) => (
                                    <TableCell key={field}>
                                        <Select
                                            size="small"
                                            displayEmpty
                                            sx={{ ...filterField, minWidth: 180 }}
                                            value={valueOf(student, field)}
                                            onChange={(e) => change(student, field, String(e.target.value))}
                                        >
                                            {PRACTICE_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {optionLabel(option.labelId, option.point)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                ))}

                                <TableCell>
                                    <Box sx={{ fontWeight: 600 }}>{weekPoint}</Box>
                                    <Box sx={{ fontSize: '12px', color: ui.muted }}>/ {WEEK_MAX}</Box>
                                </TableCell>

                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        inputProps={{ min: 0, max: 20 }}
                                        sx={{ width: 90 }}
                                        InputProps={{ sx: filterField }}
                                        value={valueOf(student, 'zachetPoint')}
                                        onChange={(e) => change(student, 'zachetPoint', e.target.value, true)}
                                    />
                                </TableCell>

                                <TableCell align="right" sx={{ minWidth: 120 }}>
                                    {rowStatus(student)}
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>

            <Box sx={{ p: 2, fontSize: '12px', color: ui.muted, display: 'flex', justifyContent: 'space-between' }}>
                <span>{formatMessage({ id: 'practicePointsHint' })}</span>
                <span>{formatMessage({ id: 'autoSaveHint' })}</span>
            </Box>
        </TableContainer>
    )
}

export default PracticeTable
